import { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarGroup } from "./ProjectAvatar";
import { DeadlineBadge } from "./DeadlineBadge";
import { VarianceBadge } from "./VarianceBadge";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { ClickableRow } from "@/components/shared/ClickableRow";
import type { UserLookup } from "@/hooks/use-users";
import type { ProjectHours } from "@/hooks/use-project-hours";
import type { ProjectProgress } from "@/hooks/use-project-progress";
import type { DBProject } from "@/lib/db-types";
import { ProjectProgressBar } from "@/components/shared/ProjectProgressBar";
import { getInvoiceStatus } from "@/lib/invoice-status";
import { estimertTimepris, reellTimepris } from "@/lib/project-rates";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { ProjectUpdatesButton } from "./ProjectUpdatesButton";
import { ProjectUpdatesDialog } from "@/components/project/ProjectUpdatesDialog";
import { useProjectCommentUnread } from "@/hooks/use-project-comment-reads";

import { type SortKey, type SortDir, nextSortState, sortActiveProjects } from "./project-sort";

/** Timepris formatert som i prosjektkortet: «—» når den ikke er målbar. */
function formatRate(rate: number | null): string {
  return rate == null ? "—" : Math.round(rate).toLocaleString("nb-NO");
}

/** Format deadline as short Norwegian date, e.g. "15. apr 2026" */
function formatDeadline(val: string | null | undefined): string {
  if (!val) return "—";
  try {
    return format(parseISO(val), "d. MMM yyyy", { locale: nb });
  } catch {
    return "—";
  }
}

/**
 * VIB-1297: kommende-prosjekter med estimert oppstart satt viser oppstart i
 * stedet for (typisk tomt) deadline-felt, samme celle gjenbrukes.
 */
function formatDeadlineOrStartLabel(p: { project_status?: string | null; estimated_start_date?: string | null }): string {
  return p.project_status === "kommende" && p.estimated_start_date ? "Oppstart" : "Deadline";
}
function formatDeadlineOrStartValue(p: {
  project_status?: string | null;
  estimated_deadline: string | null;
  estimated_start_date?: string | null;
}): string {
  if (p.project_status === "kommende" && p.estimated_start_date) {
    return formatDeadline(p.estimated_start_date);
  }
  return formatDeadline(p.estimated_deadline);
}

/** Format estimated GEEK in whole thousands, e.g. 125 000 → "125k". */
function formatBfEst(val: number | null | undefined): string {
  if (val == null) return "—";
  const thousands = Math.round(val / 1000);
  return `${thousands.toLocaleString("nb-NO")}k`;
}

/** VIB-1189: faktureringsstatus-chip basert på fakturert vs. kontrakt (delt helper). */
function InvoiceStatusBadge({ invoiced, total }: { invoiced: number; total: number }) {
  const s = getInvoiceStatus(invoiced, total);
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap", s.bg, s.text)}>
      {s.label}
    </span>
  );
}

type ProjectTableRow = Pick<
  DBProject,
  | "id"
  | "project_code"
  | "name"
  | "total_amount"
  | "bf_estimated"
  | "bf_actual"
  | "estimated_deadline"
  | "production_lead_id"
  | "advisor_id"
> & Partial<Pick<DBProject, "estimated_start_date" | "project_status" | "project_type">>;

export function ProjectTable({ title, projects, ownersMap, userMap, hoursMap, progressMap, invoicedMap = new Map(), navigate, activeProjectId, compact = false, sortKey: controlledSortKey, sortDir: controlledSortDir, onToggleSort }: {
  title: string;
  projects: ProjectTableRow[];
  ownersMap: Map<string, string[]>;
  userMap: Map<string, UserLookup>;
  hoursMap: Map<string, ProjectHours>;
  progressMap: Map<string, ProjectProgress>;
  /** VIB-1189: fakturert beløp (eks. mva) per prosjekt → faktureringsstatus. */
  invoicedMap?: Map<string, number>;
  navigate: (path: string) => void;
  activeProjectId?: string | null;
  /** Når true: vis kun Kode + Prosjekt (brukt for interne prosjekter uten økonomi). */
  compact?: boolean;
  /**
   * Controlled sortering: når onToggleSort er satt styres sorteringen utenfra
   * (ProjectsPage persisterer den over navigasjon). Ellers brukes lokal state.
   */
  sortKey?: SortKey;
  sortDir?: SortDir;
  onToggleSort?: (key: SortKey) => void;
}) {
  const [localSortKey, setLocalSortKey] = useState<SortKey>(null);
  const [localSortDir, setLocalSortDir] = useState<SortDir>("asc");
  const isControlled = !!onToggleSort;
  const sortKey = isControlled ? (controlledSortKey ?? null) : localSortKey;
  const sortDir = isControlled ? (controlledSortDir ?? "asc") : localSortDir;

  const toggleSort = (key: SortKey) => {
    if (isControlled) {
      onToggleSort!(key);
      return;
    }
    const next = nextSortState(localSortKey, localSortDir, key);
    setLocalSortKey(next.key);
    setLocalSortDir(next.dir);
  };

  // VIB-2528: sorteringen bor i project-sort.ts og deles med ProjectsPage,
  // så piltast-navigasjonen følger nøyaktig samme rekkefølge som visningen.
  const sorted = useMemo(
    () => sortActiveProjects(projects, sortKey, sortDir, { ownersMap, userMap, hoursMap, invoicedMap }),
    [projects, sortKey, sortDir, ownersMap, userMap, hoursMap, invoicedMap],
  );

  const densityClasses = "text-sm py-2 px-3";

  // VIB-1222: hurtigoppdatering — én delt dialog-instans for hele tabellen
  // i stedet for én per rad.
  const [quickUpdateProject, setQuickUpdateProject] = useState<{ id: string; name: string } | null>(null);

  // VIB-2179: ikonet åpner hele prosjektoppdaterings-dialogen og viser
  // ulest-badge (én RPC for alle radene).
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: unreadMap } = useProjectCommentUnread(projectIds);

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 gap-4">
          <h3 className="font-display text-lg text-foreground tracking-wide px-1 sm:px-0">
            {title}
          </h3>
        </div>

        {/* Mobile Card View */}
        <div className="xl:hidden space-y-3">
          {sorted.map((p) => (
            // VIB-1222: div (ikke button) — kortet trenger en nestet ekte
            // <button> for hurtigoppdatering, og button-i-button er ugyldig
            // HTML. role="button" + tabIndex + onKeyDown bevarer a11y/tastatur.
            <div
              key={p.id}
              data-project-card={p.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/prosjekter/${p.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/prosjekter/${p.id}`);
                }
              }}
              className={cn(
                "w-full text-left p-3 sm:p-4 rounded-lg border transition-colors active:bg-muted/40 min-h-[48px] touch-target cursor-pointer",
                activeProjectId === p.id
                  ? "border-primary bg-primary/5"
                  : "border-border/50 bg-card/50 hover:bg-muted/30"
              )}
            >
              <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs sm:text-sm text-primary">{p.project_code}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate text-sm sm:text-base" title={p.name}>{p.name}</p>
                    {p.project_type === "hourly" && (
                      <span
                        className="inline-flex items-center text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                        title="Time-jobb (faktureres per medgått tid)"
                      >
                        Time-jobb
                      </span>
                    )}
                  </div>
                </div>
                {/* VIB-1222/2179: prosjektoppdateringer, alltid synlig på mobilkort. */}
                <ProjectUpdatesButton
                  ariaLabel="Prosjektoppdateringer"
                  unreadCount={unreadMap?.get(p.id)?.unread_count}
                  onClick={() => setQuickUpdateProject({ id: p.id, name: p.name })}
                  className="-m-2"
                />
              </div>
              {!compact && (
                <div className="space-y-1 sm:space-y-1.5 text-xs">
                  <div className="text-muted-foreground">
                    <span className="inline-block w-16">Rådgiver:</span>
                    <span className="font-medium text-foreground">{p.advisor_id ? (userMap.get(p.advisor_id)?.full_name ?? "—") : "—"}</span>
                  </div>
                  <div className="text-muted-foreground">
                    <span className="inline-block w-16">GEEK est.:</span>
                    <span className="font-medium text-foreground">{formatBfEst(p.bf_estimated)}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-muted-foreground">Faktura:</span>
                    <InvoiceStatusBadge invoiced={invoicedMap.get(p.id) ?? 0} total={Number(p.total_amount ?? 0)} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-muted-foreground">{formatDeadlineOrStartLabel(p)}:</span>
                    {p.project_status === "kommende" && p.estimated_start_date ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-medium bg-amber-100 text-amber-700">
                        {formatDeadline(p.estimated_start_date)}
                      </span>
                    ) : (
                      <DeadlineBadge deadline={p.estimated_deadline} />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-muted-foreground">Avvik:</span>
                    {(() => {
                      // VIB-1229: budsjetteringsavvik = timer-overforbruk
                      // (loggede − budsjetterte)/budsjetterte × 100, konsistent
                      // med nøkkeltall-kortet. Null budsjetterte timer → «—».
                      const h = hoursMap.get(p.id);
                      const overrun =
                        h && h.budgeted > 0 ? ((h.logged - h.budgeted) / h.budgeted) * 100 : null;
                      return <VarianceBadge diffPct={overrun} />;
                    })()}
                  </div>
                  {progressMap.get(p.id) && progressMap.get(p.id)!.budgetedHours > 0 && (
                    <div className="pt-2 border-t border-border/50 mt-2">
                      <ProjectProgressBar progress={progressMap.get(p.id)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {sorted.length === 0 && (
            <div className="py-8 text-center text-muted-foreground text-sm">Ingen prosjekter</div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden xl:block overflow-x-auto">
          <div>
            <table className="w-full">
            <thead>
              <tr className="border-b border-border text-left">
                <SortableHeader label="Kode" active={sortKey === "code"} dir={sortDir} onToggle={() => toggleSort("code")} align="left" className={cn("pl-1 w-[52px]", densityClasses)} />
                <SortableHeader label="Prosjekt" active={sortKey === "name"} dir={sortDir} onToggle={() => toggleSort("name")} align="left" className={cn("", densityClasses)} />
                {/* VIB-1222: hurtighandlinger — ikke sorterbar, derfor egen th uten onClick. */}
                <th className={cn("w-10", densityClasses)} />
                {!compact && (
                  <>
                    <SortableHeader label="Salg" active={sortKey === "salg"} dir={sortDir} onToggle={() => toggleSort("salg")} align="center" className={cn("text-center w-[70px]", densityClasses)} />
                    <SortableHeader label="Rådgiver" active={sortKey === "advisor"} dir={sortDir} onToggle={() => toggleSort("advisor")} align="center" className={cn("text-center w-[50px]", densityClasses)} />
                    <SortableHeader label="PL" active={sortKey === "pl"} dir={sortDir} onToggle={() => toggleSort("pl")} align="center" className={cn("text-center w-[50px]", densityClasses)} />
                    <SortableHeader label="Timer" active={sortKey === "hours"} dir={sortDir} onToggle={() => toggleSort("hours")} align="left" className={cn("text-left w-[80px]", densityClasses)} />
                    <SortableHeader label="Est. Timepris" active={sortKey === "est_rate"} dir={sortDir} onToggle={() => toggleSort("est_rate")} align="right" className={cn("text-right w-[70px]", densityClasses)} title="BF estimert / solgte timer" />
                    <SortableHeader label="Live Timepris" active={sortKey === "live_rate"} dir={sortDir} onToggle={() => toggleSort("live_rate")} align="right" className={cn("text-right w-[70px]", densityClasses)} title="BF reell / loggede timer" />
                    <SortableHeader label="GEEK est." active={sortKey === "bf_est"} dir={sortDir} onToggle={() => toggleSort("bf_est")} align="right" className={cn("text-right w-[70px]", densityClasses)} />
                    <SortableHeader label="Faktura" active={sortKey === "faktura"} dir={sortDir} onToggle={() => toggleSort("faktura")} align="center" className={cn("text-center w-[110px]", densityClasses)} />
                    <SortableHeader label="Est. slutt faktura" active={sortKey === "deadline"} dir={sortDir} onToggle={() => toggleSort("deadline")} align="right" className={cn("text-right pr-1 w-[110px]", densityClasses)} />
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const salesIds = ownersMap.get(p.id) ?? [];
                return (
                  <ClickableRow
                    key={p.id}
                    data-project-id={p.id}
                    aria-current={activeProjectId === p.id ? "true" : undefined}
                    variant="link"
                    className={cn(
                      "border-b border-border/50 last:border-0 transition-colors",
                      activeProjectId === p.id
                        ? "bg-primary/5"
                        : "hover:bg-muted/20"
                    )}
                    onActivate={() => navigate(`/prosjekter/${p.id}`)}
                  >
                    <td className={cn("pl-1 font-medium text-primary tabular-nums", densityClasses)}>{p.project_code}</td>
                    <td className={cn("pr-3", densityClasses)}>
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium text-foreground truncate max-w-[250px]">{p.name}</p>
                        {p.project_type === "hourly" && (
                          <span
                            className="inline-flex items-center text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0"
                            title="Time-jobb (faktureres per medgått tid)"
                          >
                            Time-jobb
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={cn(densityClasses)}>
                      {/* VIB-1222/2179: prosjektoppdateringer med ulest-badge. -my-2 holder
                          radhøyden uendret på desktop. */}
                      <ProjectUpdatesButton
                        ariaLabel="Prosjektoppdateringer"
                        unreadCount={unreadMap?.get(p.id)?.unread_count}
                        onClick={() => setQuickUpdateProject({ id: p.id, name: p.name })}
                        className="-my-2"
                      />
                    </td>
                    {!compact && (
                      <>
                        <td className={cn("text-center", densityClasses)}><AvatarGroup userIds={salesIds} userMap={userMap} /></td>
                        <td className={cn("text-center", densityClasses)}><div className="flex justify-center"><Avatar user={p.advisor_id ? userMap.get(p.advisor_id) : undefined} /></div></td>
                        <td className={cn("text-center", densityClasses)}><div className="flex justify-center"><Avatar user={p.production_lead_id ? userMap.get(p.production_lead_id) : undefined} /></div></td>
                        <td className={cn("w-[80px]", densityClasses)}>
                          <ProjectProgressBar progress={progressMap.get(p.id)} />
                        </td>
                        <td className={cn("text-right tabular-nums text-muted-foreground", densityClasses)}>
                          {formatRate(estimertTimepris(p.bf_estimated, hoursMap.get(p.id)?.budgeted ?? 0))}
                        </td>
                        <td className={cn("text-right tabular-nums text-muted-foreground", densityClasses)}>
                          {formatRate(reellTimepris(p.bf_actual, hoursMap.get(p.id)?.logged ?? 0))}
                        </td>
                        <td className={cn("text-right tabular-nums font-medium", densityClasses)}>{formatBfEst(p.bf_estimated)}</td>
                        <td className={cn("text-center", densityClasses)}>
                          <InvoiceStatusBadge invoiced={invoicedMap.get(p.id) ?? 0} total={Number(p.total_amount ?? 0)} />
                        </td>
                        <td className={cn("pr-1 text-right tabular-nums text-muted-foreground", densityClasses)}>{formatDeadlineOrStartValue(p)}</td>
                      </>
                    )}
                  </ClickableRow>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={compact ? 3 : 12} className="py-8 text-center text-muted-foreground">Ingen prosjekter</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </CardContent>
      {quickUpdateProject && (
        <ProjectUpdatesDialog
          projectId={quickUpdateProject.id}
          projectName={quickUpdateProject.name}
          open={!!quickUpdateProject}
          onOpenChange={(open) => {
            if (!open) setQuickUpdateProject(null);
          }}
        />
      )}
    </Card>
  );
}
